




import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { fetchCategories } from '../../services/Apis';
import popular1 from '../../assets/popular1.png';
import popular2 from '../../assets/popular2.png';
import popular3 from '../../assets/popular3.png';
import popular4 from '../../assets/popular4.png';

const imageMap = [popular1, popular2, popular3, popular4];

const Popular = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false); // hover pause
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  useEffect(() => {
    const getCategories = async () => {
      setLoading(true);
      const data = await fetchCategories();
      const filtered = data
        .filter(cat => cat.isPopular === true)
        .map((cat, idx) => ({
          ...cat,
          image: cat.imageUrl || imageMap[idx % imageMap.length],
        }));
      setCategories(filtered);
      setLoading(false);
    };
    getCategories();
  }, []);

  // Continuous auto-scroll with pause and stop at end
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    let speed = 0.5;
    let animationId;

    const scroll = () => {
      if (scrollContainer) {
        const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;

        if (!paused && scrollContainer.scrollLeft < maxScrollLeft) {
          scrollContainer.scrollLeft += speed;
        } else if (scrollContainer.scrollLeft >= maxScrollLeft) {
          cancelAnimationFrame(animationId);
          return;
        }

        animationId = requestAnimationFrame(scroll);
      }
    };

    if (!loading && categories.length > 0) {
      animationId = requestAnimationFrame(scroll);
    }

    return () => cancelAnimationFrame(animationId);
  }, [loading, categories, paused]);

  const handleCat = (cat) => {
    // Clean up the category name by removing special characters and spaces
    const cleanName = (cat.name || cat.title || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-')          // Replace spaces with hyphens
      .replace(/-+/g, '-')           // Replace multiple hyphens with single
      .replace(/^-+|-+$/g, '');      // Remove leading/trailing hyphens
      
    navigate(`/books/${cleanName}?cat=${cat.id || ''}`);
  };

  // Loader
  const RedDotsLoader = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 220, width: '100%' }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <span className="red-dot" />
        <span className="red-dot" style={{ animationDelay: '0.2s' }} />
        <span className="red-dot" style={{ animationDelay: '0.4s' }} />
      </Box>
      <style>
        {`
          .red-dot {
            display: inline-block;
            width: 14px;
            height: 14px;
            border-radius: 50%;
            background: #CB4422;
            margin: 0 4px;
            animation: bounce 1s infinite;
          }
          @keyframes bounce {
            0%, 80%, 100% { transform: scale(1); opacity: 1; }
            40% { transform: scale(1.4); opacity: 0.7; }
          }
        `}
      </style>
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 600,
            fontFamily: 'Poppins',
            color: '#CB4422',
            borderLeft: '4px solid #CB4422',
            paddingLeft: 1,
          }}
        >
          Popular Categories
        </Typography>
        <Button
          disableFocusRipple
          disableRipple
          variant="contained"
          onClick={() => navigate('/popular-categories')}
          sx={{
            backgroundColor: 'secondary.main',
            color: '#000',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 12,
            fontFamily: 'Poppins',
            borderRadius: 1,
            px: 2,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: 'secondary.main',
              boxShadow: 'none',
            },
          }}
        >
          View All
        </Button>
      </Box>

      {loading ? (
        <RedDotsLoader />
      ) : (
        <Box
          ref={scrollRef}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          sx={{
            display: 'flex',
            overflowX: 'auto',
            scrollBehavior: 'smooth',
            gap: 3,
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {categories.map((category, index) => (
            <Box
              key={index}
              sx={{
                minWidth: 250,
                flex: '0 0 auto',
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: 1,
                cursor: 'pointer',
                transition: 'transform 0.3s ease-in-out',
                '&:hover': { transform: 'scale(1.03)' },
              }}
            >
              <img
                loading="lazy"
                src={category.image}
                alt={category.name || category.title}
                style={{
                  width: '100%',
                  height: '200px',
                  objectFit: 'cover',
                }}
                onClick={() => handleCat(category)}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  width: '100%',
                  backgroundColor: 'primary.main',
                  textAlign: 'center',
                  py: 1.5,
                }}
              >
                <Typography
                  sx={{
                    color: '#fff',
                    fontFamily: 'Poppins',
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  {category.name || category.title}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Container>
  );
};

export default Popular;


